import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import DOMPurify from "dompurify";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sanitizeInput(str: string, maxLength = 200) {
  if (!str) return "";
  // Use DOMPurify for reliable HTML stripping
  return DOMPurify.sanitize(str, { ALLOWED_TAGS: [] })
    .slice(0, maxLength)
    .trim();
}

/**
 * Ensures the provided URL is safe for use in src or href attributes.
 * Allows specific protocols and whitelisted domains.
 * Prevents javascript: and other dangerous URI schemes.
 */
export function getSafeUrl(url: string | null | undefined): string {
  if (!url) return "";
  
  const trimmedUrl = url.trim();

  // Allow relative paths (starting with / or ./ or ../)
  if (trimmedUrl.startsWith("/") || trimmedUrl.startsWith("./") || trimmedUrl.startsWith("../")) {
    return trimmedUrl;
  }

  // 🔒 Whitelist of allowed external domains for million-user production
  const ALLOWED_DOMAINS = [
    'youtube.com',
    'www.youtube.com',
    'img.youtube.com',
    'images.unsplash.com',
    'supabase.co',
    'supabase.in',
    'lh3.googleusercontent.com', // Google Profile Photos
    'firebasestorage.googleapis.com',
    'res.cloudinary.com',
    'google.com',
    'www.google.com',
    'ik.imagekit.io' // FixEveryday ImageKit CDN for Partner Photos/Docs
  ];

  try {
    const parsedUrl = new URL(trimmedUrl);
    
    // Only allow safe protocols
    const safeProtocols = ["http:", "https:", "data:", "blob:"];
    if (!safeProtocols.includes(parsedUrl.protocol)) {
      console.warn(`Blocked unsafe protocol: ${parsedUrl.protocol}`);
      return "";
    }

    // For http/https, enforce domain whitelist
    if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
      const hostname = parsedUrl.hostname.toLowerCase();
      const isProjectDomain = hostname.endsWith('.supabase.co') || hostname.endsWith('.supabase.in');
      const isWhitelisted = ALLOWED_DOMAINS.some(domain => 
        hostname === domain || hostname.endsWith('.' + domain)
      );

      if (isProjectDomain || isWhitelisted) {
        return trimmedUrl;
      }
      
      console.warn(`Blocked non-whitelisted domain: ${hostname}`);
      return "";
    }

    // data: and blob: are allowed if they were recognized as valid URLs
    return trimmedUrl;
  } catch (e) {
    console.error('URL validation failed inside getSafeUrl:', e);
    // If it's not a standard URL, it might be a malformed string or a safe non-standard path
    return "";
  }
}

/**
 * 🛡️ Security Hardened URL Sanitizer for Million-User Production
 * Standardizes URL cleaning and prevents common XSS vectors.
 */
export const sanitizeUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  const trimmedUrl = url.trim();

  // Explicitly block malicious schemes
  const lowerUrl = trimmedUrl.toLowerCase();
  if (lowerUrl.includes('javascript:') || lowerUrl.includes('data:') || lowerUrl.includes('vbscript:')) {
    // Only allow data:image/ if absolutely necessary, but safer to block all data: for now
    if (!lowerUrl.startsWith('data:image/')) {
      return null;
    }
  }

  try {
    // Handle relative paths
    if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('./') || trimmedUrl.startsWith('../')) {
       return trimmedUrl;
    }

    const parsed = new URL(trimmedUrl, globalThis.location.origin);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:' || parsed.protocol === 'blob:' || parsed.protocol === 'data:') {
      return parsed.toString();
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Sanitizes HTML content using DOMPurify.
 * Defaults to full escaping if no tags are allowed.
 */
export function getSafeText(text: string | null | undefined): string {
  if (!text) return "";
  // Use DOMPurify for industry-standard sanitization
  // By default, we escape everything (no allowed tags)
  return DOMPurify.sanitize(text, { 
    ALLOWED_TAGS: [], 
    ALLOWED_ATTR: [] 
  });
}


